<template>

    <section class="grid max-w-6xl py-12 mx-auto overflow-hidden md:py-20 gap-y-12 xl:px-16">
        <div class="grid items-center grid-cols-12 px-4 mx-auto max-w-7xl 2xl:px-0 md:gap-x-6">

            <div class="grid order-2 col-span-12 lg:col-span-6 gap-y-4 lg:order-1">
                <div v-for="(benefit, index) in leftBenefits" :key="index" class="flex items-start gap-x-2">
                    <div class="mt-1">
                        <svg width="23" height="21" viewBox="0 0 23 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <ellipse cx="11.0085" cy="10.2969" rx="11.0085" ry="10.2969" fill="#F1574B" />
                            <path
                                d="M9.42059 13.3552L6.51557 10.6379C6.39325 10.5235 6.39325 10.3233 6.51557 10.1803L6.97425 9.75126C7.09657 9.63685 7.31062 9.63685 7.46352 9.75126L9.66522 11.8106L14.5579 7.23424C14.6802 7.11984 14.8943 7.11984 15.0472 7.23424L15.5058 7.66328C15.6282 7.77769 15.6282 7.97791 15.5058 8.12092L9.90985 13.3552C9.75696 13.4696 9.5429 13.4696 9.42059 13.3552Z"
                                fill="white" />
                        </svg>
                    </div>

                    <div>
                        <div class="text-base font-bold sm:text-lg font-ww-bold">
                            {{ benefit.title }}
                        </div>

                        <div class="mt-2 text-base !leading-6 md:mt-4 sm:text-lg font-ww-regular">
                            {{ benefit.description }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="order-1 col-span-12 lg:col-span-6 lg:order-2">
                <nuxt-img :src="leftImage" sizes="xs:358px sm:453ppx md:512px lg:512px xl:444px" loading="lazy" width="444"
                    height="556" alt="brain glow up" format="webp" class="relative object-cover w-full max-w-lg mx-auto lg:max-w-auto -left-6 lg:left-0 lg:w-auto" />
            </div>

        </div>

        <div class="grid items-center grid-cols-12 px-4 mx-auto max-w-7xl 2xl:px-0 md:gap-x-16 gap-y-8">

            <div class="order-1 col-span-12 lg:col-span-6 lg:order-2">
                <nuxt-img :src="rightImage" sizes="xs:358px sm:453ppx md:512px lg:512px xl:444px"  loading="lazy" width="429" 
                    height="501" alt="brain glow up" format="webp" class="object-cover w-full max-w-lg mx-auto lg:max-w-full lg:left-0" />
            </div>


            <div class="grid order-1 col-span-12 lg:col-span-6 gap-y-4 md:order-2">
                <div v-for="(benefit, index) in rightBenefits" :key="index" class="flex items-start gap-x-2">
                    <div class="mt-1">
                        <svg width="23" height="21" viewBox="0 0 23 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <ellipse cx="11.0085" cy="10.2969" rx="11.0085" ry="10.2969" fill="#F1574B" />
                            <path
                                d="M9.42059 13.3552L6.51557 10.6379C6.39325 10.5235 6.39325 10.3233 6.51557 10.1803L6.97425 9.75126C7.09657 9.63685 7.31062 9.63685 7.46352 9.75126L9.66522 11.8106L14.5579 7.23424C14.6802 7.11984 14.8943 7.11984 15.0472 7.23424L15.5058 7.66328C15.6282 7.77769 15.6282 7.97791 15.5058 8.12092L9.90985 13.3552C9.75696 13.4696 9.5429 13.4696 9.42059 13.3552Z"
                                fill="white" />
                        </svg>
                    </div>

                    <div>
                        <div class="text-base font-bold sm:text-lg font-ww-bold">
                            {{ benefit.title }}
                        </div>

                        <div class="mt-2 text-base !leading-6 md:mt-4 sm:text-lg font-ww-regular">
                            {{ benefit.description }}
                        </div>
                    </div>
                </div>
            </div>

        </div>

    </section>

</template>

<script setup>
defineBuilderComponent({
  friendlyName: "BenefitsShowcase",
  inputs: [
    {
      name: "leftBenefits",
      type: "list",
      friendlyName: "Left Side Benefits",
      subFields: [
        {
          name: "title",
          type: "string",
          friendlyName: "Benefit Title",
          defaultValue: "Ever Walk into a Room and Forget Why?"
        },
        {
          name: "description",
          type: "string",
          friendlyName: "Benefit Description",
          defaultValue: "Discover simple \"Neurobic\" exercises you can do in minutes, proven by neuroscience to sharpen your focus, boost memory, and keep those \"senior moments\" far away."
        }
      ],
      defaultValue: [
        {
          title: "Ever Walk into a Room and Forget Why?",
          description: "Discover simple \"Neurobic\" exercises you can do in minutes, proven by neuroscience to sharpen your focus, boost memory, and keep those \"senior moments\" far away."
        },
        {
          title: "How to Rewire Your Brain—at Any Age",
          description: "Learn fun, easy-to-follow techniques that actually help create new neural pathways, enhancing your mental clarity and helping your mind feel decades younger."
        },
        {
          title: "The 4-Step Brain-Boosting Blueprint",
          description: "Forget complicated routines—this practical guide reveals four simple daily steps, scientifically backed yet easy enough for anyone to start seeing results fast."
        },
        {
          title: "Real-Life Brain Power You Can Feel",
          description: "No theories or vague promises—just clear strategies to enhance your mental agility, recall names instantly, and feel confident in any social situation."
        }
      ]
    },
    {
      name: "rightBenefits",
      type: "list",
      friendlyName: "Right Side Benefits",
      subFields: [
        {
          name: "title",
          type: "string",
          friendlyName: "Benefit Title",
          defaultValue: "The 80/20 Brain Nutrition Secret"
        },
        {
          name: "description",
          type: "string",
          friendlyName: "Benefit Description",
          defaultValue: "Eat smarter, not stricter. Discover a sustainable nutrition approach that fuels your brain without giving up life's tastiest pleasures."
        }
      ],
      defaultValue: [
        {
          title: "The 80/20 Brain Nutrition Secret",
          description: "Eat smarter, not stricter. Discover a sustainable nutrition approach that fuels your brain without giving up life's tastiest pleasures."
        },
        {
          title: "Weekly Cognitive-Enhancing Routine Done for You",
          description: "No guesswork here—get instant access to an interactive weekly plan filled with quick, enjoyable activities proven to enhance memory and sharpen your thinking."
        },
        {
          title: "Hidden Brain-Toxins Exposed",
          description: "Find out which everyday foods quietly sabotage your memory and focus, and learn how to protect your mind naturally, backed by solid research."
        },
        {
          title: "Long-Term Brain Health Made Easy",
          description: "Safeguard your cognitive future with practical daily habits designed to protect your brain's vitality, clarity, and sharpness for life."
        }
      ]
    },
    {
      name: "leftImage",
      type: "file",
      friendlyName: "Left Side Image",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
      defaultValue: "/assets/images/brain-glow-crop.png"
    },
    {
      name: "rightImage",
      type: "file",
      friendlyName: "Right Side Image",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
      defaultValue: "/assets/images/brain-glow-up.jpg"
    }
  ]
});

const props = defineProps({
  leftBenefits: {
    type: Array,
    default: () => [
      {
        title: "Ever Walk into a Room and Forget Why?",
        description: "Discover simple \"Neurobic\" exercises you can do in minutes, proven by neuroscience to sharpen your focus, boost memory, and keep those \"senior moments\" far away."
      },
      {
        title: "How to Rewire Your Brain—at Any Age",
        description: "Learn fun, easy-to-follow techniques that actually help create new neural pathways, enhancing your mental clarity and helping your mind feel decades younger."
      },
      {
        title: "The 4-Step Brain-Boosting Blueprint",
        description: "Forget complicated routines—this practical guide reveals four simple daily steps, scientifically backed yet easy enough for anyone to start seeing results fast."
      },
      {
        title: "Real-Life Brain Power You Can Feel",
        description: "No theories or vague promises—just clear strategies to enhance your mental agility, recall names instantly, and feel confident in any social situation."
      }
    ]
  },
  rightBenefits: {
    type: Array,
    default: () => [
      {
        title: "The 80/20 Brain Nutrition Secret",
        description: "Eat smarter, not stricter. Discover a sustainable nutrition approach that fuels your brain without giving up life's tastiest pleasures."
      },
      {
        title: "Weekly Cognitive-Enhancing Routine Done for You",
        description: "No guesswork here—get instant access to an interactive weekly plan filled with quick, enjoyable activities proven to enhance memory and sharpen your thinking."
      },
      {
        title: "Hidden Brain-Toxins Exposed",
        description: "Find out which everyday foods quietly sabotage your memory and focus, and learn how to protect your mind naturally, backed by solid research."
      },
      {
        title: "Long-Term Brain Health Made Easy",
        description: "Safeguard your cognitive future with practical daily habits designed to protect your brain's vitality, clarity, and sharpness for life."
      }
    ]
  },
  leftImage: {
    type: String,
    default: "/assets/images/brain-glow-crop.png"
  },
  rightImage: {
    type: String,
    default: "/assets/images/brain-glow-up.jpg"
  }
});
</script>